import api from "./client";
import axios from "axios";
import { BASE_URL } from "./client";
import type { Ocorrencia } from "./ocorrencia";


export interface AuditoriaItem {
	auditoria_id: number;
	conclusao?: string | null;
	data_hora_inicio?: string | null;
	data_hora_fim?: string | null; // ou data_hora_conclusao, dependendo da sua API
	status: "pendente" | "concluida" | "em andamento";
	usuario: {
		usuario_id: number;
		responsavel: string;
		usuario: string;
		senha: string;
		cpf: string;
		ativo: boolean;
	};
	localizacao: {
		localizacao_id: number;
		status: string;
		nome: string;
		altura: string;
		largura: string;
		comprimento: string;
		ean: string;
	};
	ocorrencias?: Ocorrencia[];
}


export type StatusAuditoria =
	| "pendente"
	| "concluida"
	| "em andamento"
	| "cancelada";


export async function buscarAuditoria(params?: {
	search?: string;
	offset?: number;
	limit?: number;
	status?: StatusAuditoria;
}) {
	try {
		const queryParams: Record<string, any> = {};

		if (params?.search) {
			queryParams.search = params.search;
		}

		queryParams.offset = String(params?.offset ?? 0);
		queryParams.limit = String(params?.limit ?? 50);

		if (
			params?.status === "pendente" ||
			params?.status === "concluida" ||
			params?.status === "em andamento"
		) {
			queryParams.status = params.status;
		}

		const response = await api.get(`/auditoria`, {
			params: queryParams,
		});

		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar auditorias:", error.message);
		if (error.response) {
			console.error("Response data:", error.response.data);
			console.error("Status:", error.response.status);
		}
		throw new Error("Falha ao carregar as auditorias.");
	}
}


// Função para verificar usuários ativos
export async function verificarUsuariosAtivos() {
	try {
		const response = await api.get("/auth/check-active-users");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao verificar usuários ativos:", error);
		throw new Error("Falha ao verificar usuários ativos.");
	}
}


// Função para verificar se um usuário específico está ativo
export async function verificarUsuarioAtivo(usuario: string) {
	try {
		const response = await api.get(`/auth/check-user-active/${usuario}`);
		return response.data;
	} catch (error: any) {
		console.error(
			`❌ API: Erro ao verificar se usuário ${usuario} está ativo:`,
			error,
		);
		throw new Error("Falha ao verificar usuário.");
	}
}


// Função para validar se o token ainda é válido
export async function validarToken(usuarioId: number) {
	try {
		const response = await api.post("/auth/validate-token", {
			usuario_id: usuarioId,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao validar token:", error);
		throw new Error("Falha ao validar token.");
	}
}


export interface ItemAuditoriaPayload {
	produto_estoque_id: number;
	quantidade: number;
	quantidades_sistema: number;
	quantidades_fisico: number;
	motivo_diferenca: string;
	acao_corretiva: string;
	estoque_anterior: number;
	estoque_novo: number;
	mais_informacoes?: string;
}


export async function registrarConferenciaAuditoria(
	auditoriaId: number,
	conclusao: string,
	itens: ItemAuditoriaPayload[],
) {
	try {
		const payload = {
			conclusao,
			itens,
		};

		const response = await api.post(
			`/auditoria/${auditoriaId}/concluir`,
			payload,
		);

		return response.data;
	} catch (err: any) {
		console.error("Erro ao concluir auditoria:", err);
		throw new Error(
			err?.response?.data?.message || "Erro ao concluir auditoria.",
		);
	}
}


// Função para verificar se é o primeiro acesso do usuário
export async function verificarPrimeiroAcesso(usuario: string) {
	try {
		const response = await api.get(
			`/usuario/verificar-primeiro-acesso/${usuario}`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao verificar primeiro acesso:", error);
		throw new Error("Falha ao verificar primeiro acesso.");
	}
}


// Função para configurar primeiro acesso
export async function configurarPrimeiroAcesso(
	usuario: string,
	nova_senha: string,
	confirmar_senha: string,
) {
	try {
		const response = await api.post("/usuario/configurar-primeiro-acesso", {
			usuario,
			nova_senha,
			confirmar_senha,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao configurar primeiro acesso:", error);
		throw new Error("Falha ao configurar primeiro acesso.");
	}
}


// Função para alterar senha do usuário
export async function alterarSenha(
	usuario_id: number,
	senha_antiga: string,
	nova_senha: string,
	confirmar_nova_senha: string,
) {
	try {
		const response = await api.post(`/usuario/${usuario_id}/alterar-senha`, {
			senha_antiga,
			nova_senha,
			confirmar_nova_senha,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao alterar senha:", error);
		throw new Error("Falha ao alterar senha.");
	}
}


export async function login(usuario: string, senha: string) {
	try {
		// Para login, usamos axios diretamente pois ainda não temos token
		const res = await axios.post(
			`${BASE_URL}/auth/login`,
			{ usuario, senha },
			{ withCredentials: true },
		);

		if (res.data && res.data.access_token) {
			return { status: 200, message: "Login realizado com sucesso!" };
		} else if (res.status === 201 && res.data) {
			return { status: 401, message: "Usuário ou senha inválido" };
		} else {
			// Caso não venha o token, retorna erro genérico
			return { status: 401, message: "Token não recebido." };
		}
	} catch (err: any) {
		console.error("Erro no login:", err);

		// Se a API retornar mensagem de erro, repassa para o front
		if (err.response && err.response.data && err.response.data.message) {
			return {
				status: err.response.status,
				message: err.response.data.message,
			};
		}

		// Se for erro de rede
		if (err.code === "NETWORK_ERROR" || err.code === "ERR_NETWORK") {
			return {
				status: 500,
				message: "Erro de conexão. Verifique sua internet.",
			};
		}

		// Erro inesperado
		return { status: 500, message: "Erro inesperado ao tentar login." };
	}
}


// Troca de unidade sem re-login (multi-tenant)
export async function switchUnit(unidade: string) {
	try {
		const response = await api.patch("/auth/unidade", { unidade });
		return response.data;
	} catch (error: any) {
		console.error("Erro ao trocar unidade:", error);
		throw new Error(
			error.response?.data?.message || "Falha ao trocar de unidade.",
		);
	}
}

// Função para buscar o perfil do usuário logado
export async function getCurrentUser() {
	try {
		const response = await api.get("/auth/profile");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar perfil do usuário:", error);

		// Se for erro 401, significa que o token expirou ou é inválido
		if (error.response?.status === 401) {
			// Redireciona para login
			window.location.href = "/login";
			throw new Error("Sessão expirada. Faça login novamente.");
		}

		// Se for erro de rede
		if (error.code === "NETWORK_ERROR" || error.code === "ERR_NETWORK") {
			throw new Error("Erro de conexão. Verifique sua internet.");
		}

		// Outros erros
		throw new Error(
			error.response?.data?.message || "Falha ao buscar perfil do usuário.",
		);
	}
}

